import { Form, InputNumber, Typography } from "antd";
import "./InputNumber.scss";

const { Text } = Typography;

export const AMOUNT_MAX_DIGITS = 15;
export const AMOUNT_MAX_VALUE = Number("9".repeat(AMOUNT_MAX_DIGITS));

const CustomInputNumber = ({
  label,
  name,
  isRequired,
  placeholder,
  rules,
  min = 0,
  max = AMOUNT_MAX_VALUE,
  maxDigits = AMOUNT_MAX_DIGITS,
  allowDecimals = false,
  maxDecimals = 2,
  formatter,
  parser,
  ...rest
}) => {
  const getDigitCount = (value) => String(value ?? "").replace(/\D/g, "").length;

  const defaultFormatter = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const strVal = String(value);

    if (allowDecimals) {
      const parts = strVal.split(".");
      const integerPart = parts[0]
        ? new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(parts[0].replace(/\D/g, "")))
        : "0";
      if (parts.length > 1) {
        const decimalPart = parts[1].slice(0, maxDecimals);
        return `${integerPart}.${decimalPart}`;
      }
      if (strVal.endsWith(".")) {
        return `${integerPart}.`;
      }
      return integerPart;
    }

    return `${new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(value))}`;
  };

  const defaultParser = (value) => {
    if (!value) return "";
    if (allowDecimals) {
      const clean = value.replace(/[^0-9.]/g, "");
      const parts = clean.split(".");
      const integerPart = (parts[0] || "").slice(0, maxDigits);
      if (parts.length > 1) {
        const decimalPart = parts.slice(1).join("").slice(0, maxDecimals);
        return `${integerPart}.${decimalPart}`;
      }
      return integerPart;
    }
    return value.replace(/\D/g, "").slice(0, maxDigits);
  };

  return (
    <Form.Item
      label={
        <Text>
          {label}
          {isRequired && <Text type="danger"> *</Text>}
        </Text>
      }
      name={name}
      rules={rules}
      className="custom-input-number-form-item"
    >
      <InputNumber
        changeOnWheel={false}
        placeholder={placeholder}
        min={min}
        max={max}
        className="custom-input-number"
        formatter={formatter || defaultFormatter}
        parser={parser || defaultParser}
        onKeyDown={(e) => {
          const allowedKeys = [
            "Backspace",
            "Delete",
            "ArrowLeft",
            "ArrowRight",
            "Tab",
          ];

          if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
            return;
          }

          const isNumber = /^[0-9]$/.test(e.key);
          const isDecimalPoint = allowDecimals && (e.key === "." || e.key === "Decimal");

          if (allowedKeys.includes(e.key)) return;

          if (isDecimalPoint) {
            const currentVal = String(e.target.value || "");
            if (currentVal.includes(".")) {
              e.preventDefault();
            }
            return;
          }

          if (!isNumber) {
            e.preventDefault();
            return;
          }

          const input = e.target;
          const selectionLength = (input.selectionEnd ?? 0) - (input.selectionStart ?? 0);
          const currentVal = String(input.value || "");
          const dotIndex = currentVal.indexOf(".");

          if (
            allowDecimals &&
            dotIndex !== -1 &&
            selectionLength === 0 &&
            (input.selectionStart ?? 0) > dotIndex &&
            currentVal.slice(dotIndex + 1).length >= maxDecimals
          ) {
            e.preventDefault();
            return;
          }

          if (selectionLength === 0 && getDigitCount(input.value) >= maxDigits) {
            e.preventDefault();
          }
        }}
        onFocus={(e) => {
          e.target?.select?.();
        }}
        onPaste={(e) => {
          if (!allowDecimals) {
            const pasteValue = e.clipboardData.getData("text");
            const digitsOnly = pasteValue.replace(/\D/g, "");

            if (!digitsOnly) {
              e.preventDefault();
              return;
            }

            if (digitsOnly.length > maxDigits) {
              e.preventDefault();
              const input = e.target;
              const truncated = digitsOnly.slice(0, maxDigits);
              const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                window.HTMLInputElement.prototype,
                "value"
              )?.set;
              nativeInputValueSetter?.call(input, truncated);
              input.dispatchEvent(new Event("input", { bubbles: true }));
            }
          } else {
            const pasteValue = e.clipboardData.getData("text");
            const clean = pasteValue.replace(/[^0-9.]/g, "");
            if (!clean) {
              e.preventDefault();
              return;
            }
            const parts = clean.split(".");
            const intPart = (parts[0] || "").slice(0, maxDigits);
            const decPart = parts.length > 1 ? parts.slice(1).join("").slice(0, maxDecimals) : "";
            const formattedPaste = parts.length > 1 ? `${intPart}.${decPart}` : intPart;

            if (formattedPaste !== pasteValue) {
              e.preventDefault();
              const input = e.target;
              const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                window.HTMLInputElement.prototype,
                "value"
              )?.set;
              nativeInputValueSetter?.call(input, formattedPaste);
              input.dispatchEvent(new Event("input", { bubbles: true }));
            }
          }
        }}
        {...rest}
      />
    </Form.Item>
  );
};

export default CustomInputNumber;
